import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import Loading from "../../common/components/loading/loading";
import {
  appendBandTooltipTargets,
  buildTooltipHtml,
  createTooltipHandlers,
  dashboardSeriesColors,
  formatTooltipCurrency,
  formatTooltipDate,
  formatTooltipNumber,
  removeTooltip,
} from "../../common/components/d3/d3ChartShared";

const DailyElectricityPricingandLoadDeltaGraph = ({ data, isLoading }) => {
  const svgRef = useRef();
  const tooltipRef = useRef(null);

  const renderChart = useCallback(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    removeTooltip(tooltipRef);

    if (!svg.node()) {
      return;
    }

    const parentContainer = svg.node().parentNode;
    const parentWidth = parentContainer.clientWidth;
    const parentHeight = parentContainer.clientHeight;
    const margin = { top: 30, right: 60, bottom: 40, left: 35 };

    const innerWidth = parentWidth - margin.left - margin.right;
    const innerHeight = parentHeight - margin.top - margin.bottom;

    if (!innerHeight || innerHeight <= 0 || !innerWidth) return;

    const rollupByDay = (rows) => {
      const dailyData = rows.reduce((acc, item) => {
        const date = new Date(item.beginDateTimeMpt).toLocaleDateString("fr-CA", {
          timeZone: "UTC",
        });
        if (!acc[date]) {
          acc[date] = { count: 0, poolPriceSum: 0, albertaInternalLoadSum: 0 };
        }
        acc[date].count++;
        acc[date].poolPriceSum += item.poolPrice;
        acc[date].albertaInternalLoadSum += item.albertaInternalLoad;
        return acc;
      }, {});

      return Object.entries(dailyData).map(([date, values]) => ({
        date: new Date(date),
        poolPrice: values.poolPriceSum / values.count,
        albertaInternalLoad: values.albertaInternalLoadSum / values.count,
      }));
    };

    const formattedData = rollupByDay(data);

    const deltaData = formattedData.slice(1).map((d, i) => ({
      date: d.date,
      poolPriceDelta: d.poolPrice - formattedData[i].poolPrice,
      albertaInternalLoadDelta:
        d.albertaInternalLoad - formattedData[i].albertaInternalLoad,
    }));

    const priceColor = dashboardSeriesColors.accentRed;
    const loadColor = dashboardSeriesColors.accentBlue;

    const chartSvg = svg
      .append("svg")
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("viewBox", `0 0 ${parentWidth} ${parentHeight}`)
      .attr("preserveAspectRatio", "none")
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    const xScale = d3
      .scaleBand()
      .domain(deltaData.map((d) => d.date))
      .range([0, innerWidth])
      .padding(0.3);

    const xCenter = (date) => xScale(date) + xScale.bandwidth() / 2;
    const maxPriceDelta = d3.max(deltaData.map((d) => Math.abs(d.poolPriceDelta)));
    const yScale = d3
      .scaleLinear()
      .domain([-1, 1].map((d) => d * maxPriceDelta))
      .nice()
      .range([innerHeight, 0]);

    chartSvg
      .append("g")
      .attr("class", "grid d3-chart-gridline")
      .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(""));

    const maxLoadDelta = d3.max(
      deltaData.map((d) => Math.abs(d.albertaInternalLoadDelta)),
    );
    const yScaleSecondary = d3
      .scaleLinear()
      .domain([-1, 1].map((d) => d * maxLoadDelta))
      .nice()
      .range([innerHeight, 0]);

    chartSvg
      .append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale).tickFormat(d3.timeFormat("%b %d")))
      .attr("class", "d3-chart-axis")
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em");

    const formatCurrency = d3.format("$,.0f");
    chartSvg
      .append("g")
      .call(d3.axisLeft(yScale).tickFormat(formatCurrency))
      .attr("class", "d3-chart-axis");

    chartSvg
      .append("g")
      .attr("transform", `translate(${innerWidth},0)`)
      .call(d3.axisRight(yScaleSecondary))
      .attr("class", "d3-chart-axis");

    chartSvg
      .append("text")
      .attr("transform", "rotate(90)")
      .attr("x", innerHeight / 2)
      .attr("y", -(innerWidth + margin.right - 10))
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .attr("class", "d3-chart-axis-label")
      .text("Load (MW)");

    chartSvg
      .append("g")
      .selectAll(".grid")
      .data(yScale.ticks())
      .enter()
      .append("line")
      .attr("class", "d3-chart-gridline")
      .attr("x1", 0)
      .attr("x2", innerWidth)
      .attr("y1", (d) => yScale(d))
      .attr("y2", (d) => yScale(d));

    chartSvg
      .selectAll(".bar.albertaInternalLoadDelta")
      .data(deltaData)
      .enter()
      .append("rect")
      .attr("class", "bar dashboard-bar albertaInternalLoadDelta")
      .attr("x", (d) => xScale(d.date))
      .attr("rx", 3)
      .attr("ry", 3)
      .attr("y", (d) =>
        d.albertaInternalLoadDelta > 0
          ? yScaleSecondary(d.albertaInternalLoadDelta)
          : yScaleSecondary(0),
      )
      .attr("width", xScale.bandwidth())
      .attr("height", (d) =>
        Math.abs(yScaleSecondary(d.albertaInternalLoadDelta) - yScaleSecondary(0)),
      )
      .attr("fill", loadColor);

    const defs = chartSvg.append("defs");
    const gradientUid = Math.random().toString(36).slice(2);
    const zeroOffset = Math.max(0, Math.min(1, yScale(0) / innerHeight));
    const positiveMidOffset = zeroOffset * 0.45;
    const negativeMidOffset = zeroOffset + (1 - zeroOffset) * 0.55;
    const gradientId = `daily-price-load-delta-price-${gradientUid}`;

    const gradient = defs
      .append("linearGradient")
      .attr("id", gradientId)
      .attr("gradientUnits", "userSpaceOnUse")
      .attr("x1", 0)
      .attr("y1", 0)
      .attr("x2", 0)
      .attr("y2", innerHeight);

    [
      { offset: 0, opacity: 0.28 },
      { offset: positiveMidOffset, opacity: 0.11 },
      { offset: zeroOffset, opacity: 0 },
      { offset: negativeMidOffset, opacity: 0.11 },
      { offset: 1, opacity: 0.28 },
    ].forEach(({ offset, opacity }) => {
      gradient
        .append("stop")
        .attr("offset", `${offset * 100}%`)
        .attr("stop-color", priceColor)
        .attr("stop-opacity", opacity);
    });

    const priceArea = d3
      .area()
      .defined((d) => Number.isFinite(d.poolPriceDelta))
      .x((d) => xCenter(d.date))
      .y0(() => yScale(0))
      .y1((d) => yScale(d.poolPriceDelta))
      .curve(d3.curveMonotoneX);

    const linePoolPriceDelta = d3
      .line()
      .defined((d) => Number.isFinite(d.poolPriceDelta))
      .x((d) => xCenter(d.date))
      .y((d) => yScale(d.poolPriceDelta))
      .curve(d3.curveMonotoneX);

    const areaLayer = chartSvg.append("g").attr("class", "dashboard-area-layer");
    areaLayer
      .append("path")
      .datum(deltaData)
      .attr("class", "d3-chart-area dashboard-area-highlight")
      .attr("fill", `url(#${gradientId})`)
      .attr("d", priceArea);

    chartSvg
      .append("path")
      .datum(deltaData)
      .attr("fill", "none")
      .attr("stroke", priceColor)
      .attr("stroke-width", 2)
      .attr("d", linePoolPriceDelta);

    const { showTooltip, hideTooltip } = createTooltipHandlers({
      tooltipRef,
    });

    appendBandTooltipTargets({
      chartSvg,
      data: deltaData,
      xScale,
      innerHeight,
      xAccessor: (d) => d.date,
      showTooltip,
      hideTooltip,
      htmlAccessor: (d) =>
        buildTooltipHtml({
          title: formatTooltipDate(d.date, "%Y-%m-%d"),
          rows: [
            {
              label: "Price Delta",
              value: formatTooltipCurrency(d.poolPriceDelta, 2),
              color: priceColor,
            },
            {
              label: "Load Delta",
              value: `${formatTooltipNumber(d.albertaInternalLoadDelta, {
                maximumFractionDigits: 0,
              })} MW`,
              color: loadColor,
            },
          ],
        }),
    });

    const legend = chartSvg
      .append("g")
      .attr("transform", `translate(${innerWidth / 2}, -10)`);

    chartSvg
      .append("text")
      .attr("x", innerWidth / 2)
      .attr("y", -margin.top / 2)
      .attr("text-anchor", "middle")
      .attr("font-size", "16px")
      .attr("font-weight", "bold")
      .text("AESO Average 7X24 Price and Load Delta")
      .attr("class", "d3-chart-title");

    legend
      .append("line")
      .attr("x1", innerWidth / 4 - 60)
      .attr("y1", 5)
      .attr("x2", innerWidth / 4 - 30)
      .attr("y2", 5)
      .attr("stroke", priceColor)
      .attr("stroke-width", 2);

    legend
      .append("text")
      .attr("x", innerWidth / 4 - 25)
      .attr("y", 10)
      .attr("font-size", "12px")
      .attr("class", "d3-chart-legend-label")
      .text("Price");

    legend
      .append("line")
      .attr("x1", innerWidth / 4 + 30)
      .attr("y1", 5)
      .attr("x2", innerWidth / 4 + 60)
      .attr("y2", 5)
      .attr("stroke", loadColor)
      .attr("stroke-width", 2);

    legend
      .append("text")
      .attr("x", innerWidth / 4 + 65)
      .attr("y", 10)
      .attr("font-size", "12px")
      .attr("class", "d3-chart-legend-label")
      .text("Load");
  }, [data]);

  useEffect(() => {
    !isLoading && renderChart();

    return () => {
      removeTooltip(tooltipRef);
    };
  }, [renderChart, isLoading]);

  useEffect(() => {
    let resizeTimeout = null;

    const svgNode = svgRef.current;
    if (!svgNode) return;

    const throttledResize = () => {
      if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
          if (!isLoading) {
            renderChart();
          }
          resizeTimeout = null;
        }, 100);
      }
    };

    const observer = new ResizeObserver(throttledResize);

    if (svgNode) {
      observer.observe(svgNode.parentNode);
    }

    return () => {
      if (svgNode && svgNode.parentNode) {
        observer.unobserve(svgNode.parentNode);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    };
  }, [renderChart, isLoading]);

  if (isLoading ?? true) return <Loading />;
  return <svg ref={svgRef} className="dashboard-d3-chart" width="100%" height="100%"></svg>;
};

export default DailyElectricityPricingandLoadDeltaGraph;

